import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';

// Blocos de KPI "kv-*" dos protótipos de relatório de veículos/clientes
// (`prototipo-relatorio-veiculos.html` / `prototipo-relatorio-clientes.html`).
// Compartilhados por `VehicleReport.js` e `CustomerReport.js`.

// Sparkline SVG (`.kv-spark`) — área + linha a partir de uma série numérica.
// Sem dados (série vazia/toda zero) não renderiza nada: nunca inventa curva.
export function Sparkline({ values }) {
    const hasData = Array.isArray(values) && values.length > 1 && values.some((v) => v > 0);
    if (!hasData) return null;
    const W = 160;
    const H = 36;
    const max = Math.max(...values);
    const pts = values.map((v, i) => [(i / (values.length - 1)) * W, H - 4 - (v / max) * (H - 8)]);
    const line = pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
    return (
        <Box component="svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true" sx={{ width: '100%', height: 36, mt: 'auto', color: 'primary.main', display: 'block' }}>
            <defs>
                <linearGradient id="kvSparkGrad" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0" stopColor="currentColor" stopOpacity=".25" />
                    <stop offset="1" stopColor="currentColor" stopOpacity="0" />
                </linearGradient>
            </defs>
            <path d={`${line} L${W} ${H} L0 ${H}Z`} fill="url(#kvSparkGrad)" />
            <path d={line} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        </Box>
    );
}

export const KvIcon = ({ icon, bg, fg }) => (
    <Box sx={{ width: 34, height: 34, borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, bgcolor: bg, color: fg }}>
        {icon}
    </Box>
);

/**
 * Hero (`.kv-hero`): bloco principal (valor grande + sparkline opcional) | bloco
 * lateral (valor + pílula % + barra bicolor + legenda + dica opcional).
 * main: { icon, iconBg, iconFg, label, labelTitle?, value, sub, growth? }
 * side: { icon, iconBg, iconFg, label, labelTitle?, value, pct, pctNode?, pctTone, barPct, barBg, barFg, legend: [{ color, text }], hint?, title? }
 */
export function KvHero({ main, side, loading }) {
    return (
        <Card sx={{ gridColumn: { xs: '1 / -1', lg: 'auto' } }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1.1fr 1fr' }, height: '100%' }}>
                <Box sx={{ p: '18px', display: 'flex', flexDirection: 'column', borderRight: { sm: '1px solid' }, borderBottom: { xs: '1px solid', sm: 'none' }, borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: 1 }}>
                        <KvIcon icon={main.icon} bg={main.iconBg} fg={main.iconFg} />
                        <Typography title={main.labelTitle} sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary' }}>{main.label}</Typography>
                    </Box>
                    {loading ? <Skeleton variant="text" width={110} height={42} /> : (
                        <Typography sx={{ fontSize: '38px', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
                            {main.value}
                        </Typography>
                    )}
                    <Typography sx={{ fontSize: '11.5px', color: 'text.disabled', m: '2px 0 8px' }}>{main.sub}</Typography>
                    {!loading && main.growth && <Sparkline values={main.growth} />}
                </Box>
                <Box sx={{ p: '18px', display: 'flex', flexDirection: 'column', gap: '6px' }} title={side.title}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: 1 }}>
                        <KvIcon icon={side.icon} bg={side.iconBg} fg={side.iconFg} />
                        <Typography title={side.labelTitle} sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary' }}>{side.label}</Typography>
                    </Box>
                    {loading ? <Skeleton variant="text" width={140} height={36} /> : (
                        <Typography component="div" sx={{ fontSize: '28px', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
                            {side.value}{' '}
                            <Box component="span" sx={{ fontSize: '13px', fontWeight: 700, color: `${side.pctTone}.main`, bgcolor: `${side.pctTone}.bg`, px: '8px', py: '2px', borderRadius: '20px', verticalAlign: 'middle', letterSpacing: 0 }}>
                                {side.pct}
                            </Box>
                        </Typography>
                    )}
                    <Box sx={{ height: 8, borderRadius: '20px', bgcolor: side.barBg, overflow: 'hidden', mt: '6px' }}>
                        <Box sx={{ height: '100%', width: `${side.barPct}%`, bgcolor: side.barFg, borderRadius: '20px' }} />
                    </Box>
                    <Box sx={{ display: 'flex', gap: '14px', fontSize: '11.5px', color: 'text.secondary', flexWrap: 'wrap' }}>
                        {side.legend.map((item) => (
                            <Box component="span" key={item.text}><Box component="i" sx={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', mr: '5px', bgcolor: item.color }} />{item.text}</Box>
                        ))}
                    </Box>
                    {side.hint && (
                        <Typography sx={{ fontSize: '11px', color: 'text.disabled', mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>{side.hint}</Typography>
                    )}
                </Box>
            </Box>
        </Card>
    );
}

// KPI de uso/perfil (`.kpi.kv-usage`): tag uppercase no topo + valor 24px + dica tracejada.
export function UsageKpi({ icon, iconBg, iconFg, tag, value, label, hint, loading }) {
    return (
        <Card>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <KvIcon icon={icon} bg={iconBg} fg={iconFg} />
                    <Typography sx={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'text.disabled' }}>{tag}</Typography>
                </Box>
                {loading ? <Skeleton variant="text" width={90} height={30} /> : (
                    <Typography sx={{ fontSize: '24px', fontWeight: 800, letterSpacing: '-0.02em', color: 'text.primary', mb: '2px', lineHeight: 1.15, fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' }}>{value}</Typography>
                )}
                <Typography sx={{ fontSize: '11.5px', color: 'text.secondary' }}>{label}</Typography>
                <Typography sx={{ fontSize: '11px', color: 'text.disabled', mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>{hint}</Typography>
            </CardContent>
        </Card>
    );
}

// Item da faixa de indicadores gerais (`.kv-item`). `children` = conteúdo extra
// abaixo da legenda (ex.: barra PF/PJ).
export function KvStripItem({ icon, iconBg, iconFg, value, label, loading, children }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', p: '16px 18px', borderRight: { sm: '1px solid' }, borderBottom: { xs: '1px solid', sm: 'none' }, borderColor: 'divider', '&:last-child': { borderRight: 'none', borderBottom: 'none' } }}>
            <KvIcon icon={icon} bg={iconBg} fg={iconFg} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
                {loading ? <Skeleton variant="text" width={90} height={28} /> : (
                    <Typography sx={{ fontSize: '21px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.15, color: 'text.primary', fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' }}>{value}</Typography>
                )}
                <Typography sx={{ fontSize: '11.5px', color: 'text.secondary' }}>{label}</Typography>
                {children}
            </Box>
        </Box>
    );
}
